import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../axios/axiosUmbraco';

export default function useUmbracoQuery(fetchId, filters = {}, take = 12, enabled = true, extractFields = '', apiVersion = 'v2') {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // Flag: true while a filter-change reset is in progress.
    // Prevents the page-change effect from firing a duplicate fetch.
    const isResettingRef = useRef(false);

    const getCurrentLanguageUmbraco = () => {
        const lang = localStorage.getItem("eahsLang") ?? "en";
        return lang === "en" ? "en-US" : "ar-AE";
    };

    const fetchNextPage = () => {
        if (!isLoading && hasMore) {
            setPage(prev => prev + 1);
        }
    };

    const reset = () => {
        setPage(0);
        setData([]);
        setHasMore(true);
        setTotal(0);
    };

    const buildFilterString = () => {
        let filterStr = '';
        Object.keys(filters).forEach(key => {
            const val = filters[key];
            if (val !== undefined) {
                // If val is empty string, we pass key: to clear the filter.
                filterStr += `&filter=${key}:${val}`;
            }
        });
        return filterStr;
    };

    const loadData = useCallback(async (currentPage, shouldAppend) => {
        if (!enabled) return;
        setIsLoading(true);

        const skip = currentPage * take;
        const filterQuery = buildFilterString();
        const fieldsQuery = extractFields ? `&fields=${extractFields}` : '';
        const url = `/umbraco/delivery/api/${apiVersion}/content?fetch=${fetchId}&skip=${skip}&take=${take}${filterQuery}${fieldsQuery}`;

        try {
            const response = await axios.get(url, {
                headers: {
                    "accept-language": getCurrentLanguageUmbraco(),
                },
            });

            const newItems = response?.data?.items || [];
            const totalItems = response?.data?.total || 0;

            if (shouldAppend) {
                setData(prev => [...prev, ...newItems]);
            } else {
                setData(newItems);
            }

            setTotal(totalItems);
            setHasMore(skip + newItems.length < totalItems);
        } catch (error) {
            console.error('Umbraco fetch error:', error);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, [fetchId, JSON.stringify(filters), take, enabled, extractFields]);

    // When filters change: reset state and fetch page 0 — single fetch only
    useEffect(() => {
        if (!enabled) return;
        isResettingRef.current = true; // mark: this page=0 comes from a filter reset
        reset();
        loadData(0, false);
    }, [JSON.stringify(filters), enabled, fetchId]);

    // When page increments via fetchNextPage: append next page
    useEffect(() => {
        if (!enabled || page === 0) return;
        if (isResettingRef.current) {
            // page was reset to 0 by a filter change — loadData already called above, skip
            isResettingRef.current = false;
            return;
        }
        loadData(page, true);
    }, [page]);

    return {
        data,
        total,
        isLoading,
        hasMore,
        fetchNextPage,
        reset
    };
}
